import { useEffect, useState } from 'react'
import { useParams, Link as RouterLink } from 'react-router-dom'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useTranslation } from 'react-i18next'
import { Box, Typography, Alert, Breadcrumbs, Skeleton, Link as MuiLink } from '@mui/material'
import { NavigateNext as NavigateNextIcon, Home as HomeIcon } from '@mui/icons-material'
import { userSettingsApi, type StudentSettingsRestrictionReason } from '@/api/userSettings'
import { useUserStore } from '@/stores/userStore'
import { SettingsForm } from '@/components/settings/SettingsForm'
import { ROUTES } from '@/utils/constants'
import { getUserDisplayName } from '@/utils/userName'
import type { UserSettings } from '@/types'
import '@/styles/dashboard.css'

export function UserSettingsPage() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const userId = id ? parseInt(id, 10) : NaN
  const { currentUser, fetchUser, error, clearError } = useUserStore()
  const [settings, setSettings] = useState<UserSettings | null>(null)
  const [restrictionReason, setRestrictionReason] = useState<StudentSettingsRestrictionReason | null>(null)
  const [settingsLoading, setSettingsLoading] = useState(true)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [settingsError, setSettingsError] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  useEffect(() => {
    if (!id || Number.isNaN(userId)) return
    fetchUser(userId).catch(() => {})
  }, [id, userId, fetchUser])

  useEffect(() => {
    if (!userId || Number.isNaN(userId)) return
    setSettingsLoading(true)
    setSettingsError(null)
    userSettingsApi
      .getUserSettings(userId)
      .then((result) => {
        setSettings(result.settings)
        setRestrictionReason(result.restrictionReason)
      })
      .catch((err: Error) =>
        setSettingsError(err.message || t('profile.settings.loadError'))
      )
      .finally(() => setSettingsLoading(false))
  }, [userId, t])

  const handleSubmit = async (
    data: Parameters<typeof userSettingsApi.updateUserSettings>[1]
  ) => {
    if (Number.isNaN(userId)) return
    setSettingsError(null)
    setSuccessMsg(null)
    setSubmitLoading(true)
    try {
      const updated = await userSettingsApi.updateUserSettings(userId, data)
      setSettings(updated)
      setSuccessMsg(t('profile.settings.saveSuccess'))
    } catch (err) {
      const message = err instanceof Error ? err.message : t('profile.settings.saveError')
      setSettingsError(message)
    } finally {
      setSubmitLoading(false)
    }
  }

  const studentDisplayName =
    getUserDisplayName(currentUser, { prefer: 'full_name' }) || t('profile.title')

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 2 }}
        >
          <MuiLink
            component={RouterLink}
            to={ROUTES.DASHBOARD}
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </MuiLink>
          <MuiLink component={RouterLink} to={ROUTES.USERS} underline="hover">
            {t('dashboard.students')}
          </MuiLink>
          <MuiLink component={RouterLink} to={`/users/${userId}`} underline="hover">
            {studentDisplayName}
          </MuiLink>
          <Typography color="text.primary">{t('users.settings')}</Typography>
        </Breadcrumbs>

        <Typography variant="h4" component="h1" gutterBottom fontWeight={700}>
          {t('users.settings')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {t('users.settingsPageSubtitle')}
        </Typography>

        {settingsError && (
          <Alert severity="error" onClose={() => setSettingsError(null)} sx={{ mb: 2 }}>
            {settingsError}
          </Alert>
        )}
        {error && (
          <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {successMsg && (
          <Alert severity="success" onClose={() => setSuccessMsg(null)} sx={{ mb: 2 }}>
            {successMsg}
          </Alert>
        )}

        {(settingsLoading || (!settings && !settingsError)) && (
          <Box>
            <Skeleton variant="text" width={220} height={32} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" height={56} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" height={56} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" height={56} sx={{ mb: 2 }} />
            <Skeleton variant="rounded" width={160} height={40} />
          </Box>
        )}

        {!settingsLoading && settings && (
          <>
            <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
              {t('users.settingsSectionTests')}
            </Typography>
            <SettingsForm
              initialData={settings}
              onSubmit={handleSubmit}
              isLoading={submitLoading}
              studentSettingsRestriction={restrictionReason}
            />
          </>
        )}
      </Box>
    </DashboardLayout>
  )
}
